/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import azCommonStyles from '../../../theme/globals.module.scss';
import ContentContainer from '../ContentContainer';
import styles from './styles.module.scss';
import { CheckoutSmartLink } from './CheckoutSmartLink';
import { Close } from '@az/starc-ui-icons';
import { Actionable, Icon } from '@az/starc-ui';
import { scrollIntoViewWithOffset } from '@/utils/domutils';
import { useLabels } from '@/hooks/useLabels';

export type NotificationType = 'error' | 'warn' | 'info' | 'success';

type Props = {
  message: string | React.ReactNode;
  subMessage?: React.ReactNode | string;
  type: NotificationType;
  itemLevel?: boolean;
  scrollToNotification?: boolean;
  id?: string;
  customClasses?: string;
  notificationContainerClassName?: string;
  errorFont?: boolean;
  checkoutSmartLink?: boolean;
  smartLinkMessage?: string;
  withPadding?: boolean;
  hasCloseIcon?: boolean;
  disableLiveRegion?: boolean;
};

const labelMap = {
  labelAriaLabelCloseNotification: 'label_aria_label_close_notification',
};

export function NotificationBlock({
  message,
  subMessage = '',
  type,
  itemLevel = false,
  scrollToNotification = false,
  id,
  customClasses,
  notificationContainerClassName,
  errorFont = false,
  checkoutSmartLink,
  smartLinkMessage,
  withPadding = true,
  hasCloseIcon = false,
  disableLiveRegion = false,
}: Props) {
  const labels = useLabels(labelMap);
  const [open, setOpen] = useState(true);
  const alertRef = useRef<HTMLDivElement | null>(null);

  const baseClass = itemLevel
    ? styles[`${String(type)}ItemLevel`]
    : styles[`${String(type)}Shadow`];

  const alertRole = !disableLiveRegion && type === 'error' ? 'alert' : undefined;
  const ariaLive = !disableLiveRegion ? (type === 'error' ? 'assertive' : 'polite') : undefined;
  const ariaLabel = useMemo(() => {
    if (typeof message !== 'string') {
      return '';
    }

    const startTag = '<phone>';
    const endTag = '</phone>';

    const startIndex = message.indexOf(startTag);
    const endIndex = message.indexOf(endTag);

    if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) {
      return '';
    }

    const phoneNumber = message.substring(startIndex + startTag.length, endIndex);

    if (!phoneNumber.trim()) {
      return '';
    }

    return `${message.substring(0, startIndex).trim()} ${phoneNumber}`.trim();
  }, [message]);

  const isChrome = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  }, []);

  useEffect(() => {
    if (alertRef.current) {
      alertRef.current.setAttribute('tabindex', '-1');
      alertRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!scrollToNotification || !alertRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (alertRef.current && scrollToNotification) {
        scrollIntoViewWithOffset(alertRef.current, 100, {
          behavior: 'smooth',
        });
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [scrollToNotification]);

  const handleOnCloseClick = () => {
    setOpen(false);
  };

  if (!open) {
    return null;
  }

  return (
    <>
      {ariaLabel.length > 0 && !isChrome ? (
        <div
          ref={alertRef}
          tabIndex={-1}
          role={alertRole}
          aria-live={ariaLive}
          aria-atomic={!disableLiveRegion ? 'true' : undefined}
          className={cx(styles.notificationContainer, notificationContainerClassName)}
          aria-label={ariaLabel}
        >
          <ContentContainer
            type="shadowed"
            withPadding={withPadding}
            withMargin={!itemLevel}
            className={customClasses ? `${baseClass} ${customClasses}` : `${baseClass}`}
            id="notificationAlert"
            data-testid="notificationAlert"
          >
            <div className={cx(azCommonStyles['az-caption'], styles.notificationMessage)}>
              <div className={styles.oldAlertStyle}>
                <span className={styles[type]} />
              </div>
              <span
                id={id ? id : ''}
                className={cx([
                  checkoutSmartLink || errorFont ? styles.errorFont : '',
                  styles.errorFont,
                ])}
              >
                {message}
                {checkoutSmartLink && <CheckoutSmartLink smartLinkMessage={smartLinkMessage} />}
              </span>{' '}
              {hasCloseIcon && (
                <Actionable
                  onClick={handleOnCloseClick}
                  tabIndex={0}
                  attributes={{ ariaLabel: labels.labelAriaLabelCloseNotification }}
                >
                  <Icon
                    svg={Close}
                    size={5}
                    attributes={{
                      style: { color: '#202124', margin: '6px' },
                    }}
                  />
                </Actionable>
              )}
            </div>
          </ContentContainer>
        </div>
      ) : (
        <div
          ref={alertRef}
          tabIndex={-1}
          role={alertRole}
          aria-live={ariaLive}
          aria-atomic={!disableLiveRegion ? 'true' : undefined}
          className={cx(styles.notificationContainer, notificationContainerClassName)}
        >
          <ContentContainer
            type="shadowed"
            withPadding={withPadding}
            withMargin={!itemLevel}
            className={
              checkoutSmartLink
                ? `${styles.smartLinkItemLevel}`
                : customClasses
                  ? `${baseClass} ${customClasses}`
                  : `${baseClass}`
            }
            id="notificationAlert"
            data-testid="notificationAlert"
          >
            <div className={cx(azCommonStyles['az-caption'], styles.notificationMessage)}>
              <div className={styles.oldAlertStyle}>
                <span className={styles[type]} />
              </div>

              <span
                id={id ? id : ''}
                className={cx([
                  checkoutSmartLink || errorFont ? styles.errorFont : '',
                  styles.errorFont,
                ])}
              >
                {message} {subMessage}
                {checkoutSmartLink && <CheckoutSmartLink smartLinkMessage={smartLinkMessage} />}
              </span>
              {hasCloseIcon && (
                <Actionable
                  onClick={handleOnCloseClick}
                  tabIndex={0}
                  attributes={{ ariaLabel: labels.labelAriaLabelCloseNotification }}
                >
                  <Icon
                    svg={Close}
                    size={5}
                    attributes={{
                      style: { color: '#202124', margin: '6px' },
                    }}
                  />
                </Actionable>
              )}
            </div>
          </ContentContainer>
        </div>
      )}
    </>
  );
}

export default NotificationBlock;
